import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Status, Tag, TextInput, Tile, Tiles } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';

/**
 * Screen 6: the edits.
 *
 * Maestro records edits and never runs them (rule 9) — there is no launch
 * button anywhere on this screen, and the server refuses it too. What it does
 * is tell you which clips point at a movie that is no longer the good one, and
 * which it could not match at all.
 *
 * An import is `xmeml` — Final Cut Pro 7 XML, which is what Premiere's "Final
 * Cut Pro XML" export writes. Not `.fcpxml`, which is a different format
 * entirely (docs/06, decision 8).
 */

interface Clip {
  source_clip_ref: string;
  match: 'path' | 'name' | 'manual' | 'none';
  vc_pass_id?: string;
  movie_id?: string;
}

interface Edit {
  _id: string;
  name: string;
  version: number;
  source: { type: 'manual' | 'circe' | 'fcpxml'; ref?: string; imported_at?: string; imported_by?: string };
  clips: Clip[];
}

const SOURCE_WORDS: Record<string, string> = {
  manual: 'made by hand',
  circe: 'imported from Circe',
  fcpxml: 'imported from Premiere'
};

export function Edits({ projectId }: { projectId: string }) {
  const can = useCan();
  const [toast, setToast] = useState<string | null>(null);
  const [importing, setImporting] = useState<{ name: string; xml: string } | null>(null);

  const { state, reload } = useLoad<{ edits: Edit[] }>(
    () => api.get(`/api/edits?project_id=${encodeURIComponent(projectId)}`),
    [projectId]
  );
  const { state: needsYou } = useLoad<{ clips: { edit_id: string; edit_name: string; source_clip_ref: string }[] }>(
    () => api.get(`/api/edits/needs-you?project_id=${encodeURIComponent(projectId)}`),
    [projectId]
  );
  const { state: older } = useLoad<{ clips: { edit_id: string; edit_name: string; source_clip_ref: string }[] }>(
    () => api.get(`/api/edits/older-movies?project_id=${encodeURIComponent(projectId)}`),
    [projectId]
  );

  const edits = state.data?.edits ?? [];
  const unmatched = needsYou.data?.clips ?? [];
  const onOlder = older.data?.clips ?? [];

  const importXml = async (): Promise<void> => {
    if (!importing) return;
    try {
      await api.post('/api/edits/import/premiere', {
        project_id: projectId,
        name: importing.name,
        xml: importing.xml
      });
      setImporting(null);
      setToast('Imported. Notes on this edit were kept.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That import was refused.');
    }
  };

  return (
    <Page
      eyebrow="MAESTRO · EDITS"
      title="Edits"
      description="A list of clips pointing at VC-pass movies. Maestro records edits and never runs them."
      note="An import reads Final Cut Pro 7 XML, which is what Premiere's “Final Cut Pro XML” export writes."
      action={
        can.import_edit ? (
          <Button tone="primary" onClick={() => setImporting({ name: '', xml: '' })}>
            Import from Premiere
          </Button>
        ) : undefined
      }
    >
      <Tiles>
        <Tile count={edits.length} label="Edits" />
        <Tile count={unmatched.length} label="Clips nobody has matched" swatch={colour.stale} />
        <Tile count={onOlder.length} label="Clips using an older movie" swatch={colour.stale} />
      </Tiles>

      {state.status === 'loading' && edits.length === 0 && <Loading says="Loading the edits…" rows={4} />}
      {state.status === 'failed' && edits.length === 0 && (
        <CouldNotLoad what="the edits" error={state.error} onRetry={reload} />
      )}
      {state.status !== 'loading' && edits.length === 0 && state.status !== 'failed' && (
        <Empty
          title="No edits yet"
          says="Import one from Premiere or Circe, or make one by hand. Maestro records them; it never runs them."
        />
      )}

      {edits.map((edit) => {
        const bad = edit.clips.filter((c) => c.match === 'none').length;
        return (
          <Card
            key={edit._id}
            title={edit.name}
            aside={`version ${edit.version} · ${SOURCE_WORDS[edit.source.type] ?? edit.source.type}`}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
              <span style={{ ...text.small, color: colour.inkSoft }}>
                {edit.clips.length} {edit.clips.length === 1 ? 'clip' : 'clips'}
              </span>
              {bad > 0 && <Status value="bad" detail={`${bad} not matched`} />}
              {edit.source.imported_by && (
                <span style={{ ...text.note, color: colour.inkMuted }}>
                  by {edit.source.imported_by}
                  {edit.source.imported_at ? ` · ${new Date(edit.source.imported_at).toLocaleDateString()}` : ''}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {edit.clips.slice(0, 24).map((clip) => (
                <Tag key={clip.source_clip_ref} tone="mono">
                  {clip.source_clip_ref}
                  {clip.match === 'none' ? ' · ?' : ` · ${clip.match}`}
                </Tag>
              ))}
              {edit.clips.length > 24 && (
                <span style={{ ...text.note, color: colour.inkFaint }}>
                  and {edit.clips.length - 24} more
                </span>
              )}
            </div>
          </Card>
        );
      })}

      {unmatched.length > 0 && (
        <Card title="Clips nobody has matched" aside="Maestro could not work out which movie these are.">
          {unmatched.map((clip) => (
            <div
              key={`${clip.edit_id}-${clip.source_clip_ref}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}
            >
              <span style={text.monoStrong}>{clip.source_clip_ref}</span>
              <span style={{ ...text.note, color: colour.inkSoft }}>in {clip.edit_name}</span>
            </div>
          ))}
        </Card>
      )}

      {onOlder.length > 0 && (
        <Card title="Clips using an older movie" aside="The edit still plays; it is not showing the current version.">
          {onOlder.map((clip) => (
            <div
              key={`${clip.edit_id}-${clip.source_clip_ref}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}
            >
              <span style={text.monoStrong}>{clip.source_clip_ref}</span>
              <span style={{ ...text.note, color: colour.inkSoft }}>in {clip.edit_name}</span>
              <Status value="stale" detail="older movie" />
            </div>
          ))}
        </Card>
      )}

      {importing && (
        <Dialog
          title="Import an edit from Premiere"
          says="Export “Final Cut Pro XML” from Premiere and paste it here. Re-importing an edit of the same name replaces its clips and keeps its notes."
          confirm="Import"
          onConfirm={() => void importXml()}
          onClose={() => setImporting(null)}
        >
          <Field label="What to call it">
            <TextInput
              value={importing.name}
              onChange={(name) => setImporting({ ...importing, name })}
              placeholder="reel_01"
            />
          </Field>
          <Field label="The XML">
            <textarea
              value={importing.xml}
              onChange={(e) => setImporting({ ...importing, xml: e.target.value })}
              rows={8}
              placeholder="<?xml version=&quot;1.0&quot;?><!DOCTYPE xmeml><xmeml version=&quot;5&quot;>…"
              style={{
                padding: 10,
                borderRadius: 8,
                border: `1px solid ${colour.edge}`,
                font: text.mono.font,
                resize: 'vertical'
              }}
            />
          </Field>
        </Dialog>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
